import { Component, signal, inject, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LogEntry, DebugLoggerService } from '../../../core/services/dev-tool/debug-logger.service';
import { JsonTreeComponent } from '../json-tree/json-tree.component';
import { SplitterModule } from '@syncfusion/ej2-angular-layouts';

@Component({
  selector: 'app-console-view',
  standalone: true,
  imports: [CommonModule, FormsModule, JsonTreeComponent, SplitterModule],
  templateUrl: './console-view.component.html',
  styleUrls: ['./console-view.component.scss']
})
export class ConsoleViewComponent {
  private readonly logger = inject(DebugLoggerService);

  // Input: Available height from parent
  contentHeight = input<number>(500); // Default fallback

  // Filter state
  filters = signal({
    error: true,
    api: true,
    warn: true,
    info: true
  });

  searchQuery = signal('');
  showTimestamp = signal(false);

  // Get all logs from service
  allLogs = this.logger.getLogs;

  // Computed counts
  errorCount = computed(() =>
    this.allLogs().filter((l: LogEntry) => l.type === 'error').length
  );

  apiCount = computed(() =>
    this.allLogs().filter((l: LogEntry) => l.type === 'api').length
  );

  warnCount = computed(() =>
    this.allLogs().filter((l: LogEntry) => l.type === 'warn').length
  );

  infoCount = computed(() =>
    this.allLogs().filter((l: LogEntry) => l.type === 'info' || l.type === 'log').length
  );

  // Filtered logs with pre-computed properties
  filteredLogs = computed(() => {
    const filterState = this.filters();
    const query = this.searchQuery().toLowerCase();
    const logs = this.allLogs();
    const expanded = this.expandedLogs();

    return logs
      .filter((log: LogEntry) => {
        // Check if log message matches search query
        const matchesSearch = !query || log.message.toLowerCase().includes(query);

        // Always show command and result types if they match the search
        if (log.type === 'command' || log.type === 'result') {
          return matchesSearch;
        }

        // Map 'log' type to 'info' filter (info + log combined)
        let filterKey: 'error' | 'api' | 'warn' | 'info' = log.type === 'log' ? 'info' : log.type as any;

        // For other types, check the filter and search
        const typeMatch = filterState[filterKey];
        return typeMatch && matchesSearch;
      })
      .map((log: LogEntry) => {
        // Check if data is JSON-like and not empty
        const isJsonData = this.checkIsJsonLike(log.data);
        const hasJsonContent = isJsonData && this.hasContent(log.data);

        return {
          ...log,
          data: this.parseLogData(log.data),
          blobPreviewUrl: log.data?.responseBody?._blobUrl || null,
          queryName: this.computeQueryName(log),
          isJsonData: hasJsonContent,
          isExpanded: expanded.has(log.id),
          inlinePreview:this.computeInlinePreview(log.data)
        };
      });
  });

  command = '';
  commandHistory: string[] = [];
  historyIndex = -1;
  expandedLogs = signal(new Set<string>());

  toggleFilter(type: 'error' | 'warn' | 'api' | 'info') {
    this.filters.update(f => ({
      ...f,
      [type]: !f[type]
    }));
  }

  clearLogs() {
    this.logger.clear();
    this.expandedLogs.set(new Set<string>());
  }

  toggleExpand(id: string) {
    this.expandedLogs.update(set => {
      const newSet = new Set(set);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  // Handle click on message text - only toggle if not selecting text
  onMessageClick(id: string) {
    // Check if there's a text selection
    const selection = window.getSelection();
    const hasSelection = selection && selection.toString().length > 0;

    // Only toggle if no text was selected
    if (!hasSelection) {
      this.toggleExpand(id);
    }
  }

  // Check if value is JSON-like (object or array, not null)
  private checkIsJsonLike(value: any): boolean {
    return value !== null && typeof value === 'object';
  }

  // Check if JSON data has content (not empty array/object)
  private hasContent(data: any): boolean {
    if (!this.checkIsJsonLike(data)) return false;

    if (Array.isArray(data)) {
      return data.length > 0;
    }

    const keys = Object.keys(data || {});
    return keys.length > 0;
  }

  // Compute inline preview for JSON data
  private computeInlinePreview(data: any): string | null {
    if (!this.checkIsJsonLike(data)) return null;
    return this.getPreviewString(data);
  }

  // Parse string fields (requestBody, responseBody) in API log data so JSON tree can render them as expandable objects
  private parseLogData(data: any): any {
    if (!data || typeof data !== 'object') return data;

    const parsed = { ...data };
    if (typeof parsed.requestBody === 'string') {
      try { parsed.requestBody = JSON.parse(parsed.requestBody); } catch {}
    }
    if (typeof parsed.responseBody === 'string') {
      try { parsed.responseBody = JSON.parse(parsed.responseBody); } catch {}
    }
    // For blob responses, show clean metadata in tree (hide internal _blobUrl/_isBlob)
    if (parsed.responseBody?._isBlob) {
      parsed.responseBody = {
        type: parsed.responseBody.type,
        size: parsed.responseBody.sizeFormatted
      };
    }
    return parsed;
  }

  // Extract query name from log data
  private computeQueryName(log: LogEntry): string | null {
    if (log.data && log.data.url) {
      try {
        const url = log.data.url;
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        const segments = path.split('/').filter(s => s);

        if (segments.length > 0) {
          const lastSegment = segments[segments.length - 1];
          if (!isNaN(Number(lastSegment)) && segments.length > 1) {
            return segments[segments.length - 2];
          }
          return lastSegment;
        }
      } catch {
        return null;
      }
    }
    return null;
  }

  executeCommand() {
    if (!this.command.trim()) return;

    this.commandHistory.push(this.command);
    this.historyIndex = this.commandHistory.length;

    this.logger.executeCode(this.command);
    this.command = '';
  }

  navigateHistory(direction: number) {
    if (this.commandHistory.length === 0) return;

    this.historyIndex += direction;

    if (this.historyIndex < 0) {
      this.historyIndex = 0;
    } else if (this.historyIndex >= this.commandHistory.length) {
      this.historyIndex = this.commandHistory.length;
      this.command = '';
      return;
    }

    this.command = this.commandHistory[this.historyIndex];
  }

  private getPreviewString(value: any): string {
    if (Array.isArray(value)) {
      const len = value.length;
      if (len === 0) return '[]';
      if (len <= 3) {
        return `[${value.map(v => this.previewPrimitive(v)).join(', ')}]`;
      }
      return `[${this.previewPrimitive(value[0])}, ${this.previewPrimitive(value[1])}, …]`;
    }

    // object
    const keys = Object.keys(value || {});
    if (keys.length === 0) return '{}';
    if (keys.length <= 2) {
      return `{ ${keys
        .map(k => `${k}: ${this.previewPrimitive(value[k])}`)
        .join(', ')} }`;
    }
    const firstKey = keys[0];
    return `{ ${firstKey}: ${this.previewPrimitive(value[firstKey])}, … }`;
  }

  private previewPrimitive(v: any): string {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    if (typeof v === 'string') {
      const s = v.length > 20 ? v.slice(0, 20) + '…' : v;
      return `"${s}"`;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) return `[${v.length}]`;
    if (typeof v === 'object') return '{…}';
    return '…';
  }

}